import moment from 'moment'

export type HistoryEntry = { endOfTradingDate: string; totalValue: number; securityCode?: string }
export type TimePoint = { x: number; y: number; percent?: number | null }

/**
 * Generates an inclusive array of dates from start to end in YYYY-MM-DD format.
 * @param start - Start date (moment, string, or Date object)
 * @param end - End date (moment, string, or Date object)
 * @returns Array of date strings, or empty array if dates are invalid or start > end
 */
export function generateDateRange(start: moment.Moment | string | Date, end: moment.Moment | string | Date) {
    const s = moment(start).startOf('day')
    const e = moment(end).startOf('day')
    const out: string[] = []
    if (!s.isValid() || !e.isValid() || s.isAfter(e)) return out
    const cur = s.clone()
    while (cur.isSameOrBefore(e, 'day')) {
        out.push(cur.format('YYYY-MM-DD'))
        cur.add(1, 'day')
    }
    return out
}

/**
 * Builds a time series with forward-filled values and percent changes.
 * @param history - Array of historical data points with dates and values
 * @param start - Start date for the time series
 * @param end - End date for the time series
 * @param holdingsAggregate - Optional current holdings value to use for the last point
 * @param placeholderValue - Default value when no data exists (default: 0)
 * @returns Array of time points with x (timestamp), y (value), and percent (change from previous)
 */
export function buildTimeSeries(
    history: HistoryEntry[] | undefined,
    start: moment.Moment | string | Date,
    end: moment.Moment | string | Date,
    holdingsAggregate?: number | null,
    placeholderValue = 0,
): TimePoint[] {
    const dates = generateDateRange(start, end)
    const map = new Map<string, number>()
    ;(history || []).forEach((h) => {
        // parse using strict YYYY-MM-DD to avoid Moment falling back to JS Date()
        const raw = h?.endOfTradingDate
        const parsed = typeof raw === 'string' && moment(raw, 'YYYY-MM-DD', true)
        if (parsed && parsed.isValid()) {
            const key = parsed.format('YYYY-MM-DD')
            map.set(key, Number(h.totalValue) || 0)
        }
    })

    const out: TimePoint[] = []
    let prev = 0
    // initialize prev from the earliest known history value if available
    if (map.size > 0) {
        const firstKey = Array.from(map.keys()).sort()[0]
        const firstVal = map.get(firstKey)
        if (typeof firstVal === 'number' && Number.isFinite(firstVal)) {
            prev = firstVal as number
        }
    }
    for (let i = 0; i < dates.length; i++) {
        const d = dates[i]
        if (map.has(d)) {
            prev = map.get(d) as number
        }
        out.push({ x: Date.parse(d), y: prev, percent: null })
    }

    // apply holdingsAggregate or placeholder to last point as the component expects
    if (out.length > 0) {
        const lastIndex = out.length - 1
        if (holdingsAggregate != null && holdingsAggregate > 0) {
            out[lastIndex].y = holdingsAggregate
        } else if (out[lastIndex].y === 0) {
            out[lastIndex].y = placeholderValue
        }
    }

    // compute percents in a single pass
    for (let i = 0; i < out.length; i++) {
        const cur = out[i]
        const prevPoint = i > 0 ? out[i - 1] : null
        let pct: number | null = null
        if (prevPoint && Number.isFinite(prevPoint.y)) {
            if (prevPoint.y === 0) pct = 0
            else pct = ((cur.y - prevPoint.y) / Math.abs(prevPoint.y)) * 100
        }
        cur.percent = pct
    }

    return out
}

export default {
    generateDateRange,
    buildTimeSeries,
}
